// Free-gift (freebie) tiers for the cart drawer progress bar.
// Freebies are ₹0 Shopify products auto-added as cart lines once the
// merchandise subtotal (excl. warranty add-ons and freebies themselves)
// crosses a tier threshold. Handles are exact Shopify handles (SSOT).
import { API_BASE } from "./apiBase";
import { isWarrantyLine } from "./warrantyAddon";

const MYSTERY = "mystery-freebie";

export const FREEBIE_HANDLES = [
        "keycap-keychain",
        "karambit-keychain",
        "dot-skates",
        "he-switches-4pk",
        MYSTERY,
];
const FREEBIE_SET = new Set(FREEBIE_HANDLES);

export const FREEBIE_TIERS = [
        { tier: 1, min: 3000 },
        { tier: 2, min: 7000 },
        { tier: 3, min: 12000 },
];

const LABELS = {
        "keycap-keychain": "Keycap Keychain",
        "karambit-keychain": "Karambit Keychain",
        "dot-skates": "Dot Skates",
        "he-switches-4pk": "HE Switches (4-pack)",
        [MYSTERY]: "Mystery Freebie",
};

export function freebieLabelFor(handle) {
        return LABELS[handle] || "Free Gift";
}

export function lineHandle(line) {
        return line?.merchandise?.product?.handle || "";
}

export function isFreebieLine(line) {
        if (FREEBIE_SET.has(lineHandle(line))) return true;
        const attrs = line?.attributes || [];
        return attrs.some((a) => a && a.key === "_intent" && a.value === "freebie");
}

export function freebieTierOf(line) {
        const h = lineHandle(line);
        if (h === "keycap-keychain") return 1;
        if (h === "karambit-keychain") return 2;
        if (h === "dot-skates" || h === "he-switches-4pk") return 3;
        const attrs = line?.attributes || [];
        const t = (attrs.find((a) => a && a.key === "_freebie_tier") || {}).value;
        return Number(t) || 0;
}

// Merchandise subtotal — everything except warranty add-ons and free gifts.
export function merchSubtotal(lines) {
        return (lines || []).reduce((acc, l) => {
                if (isWarrantyLine(l) || isFreebieLine(l)) return acc;
                const price = Number(l?.merchandise?.price?.amount || 0);
                return acc + price * (l?.quantity || 0);
        }, 0);
}

export function tierFor(subtotal) {
        if (subtotal >= 12000) return 3;
        if (subtotal >= 7000) return 2;
        if (subtotal >= 3000) return 1;
        return 0;
}

// Tier-3 gift depends on cart composition: a mouse in cart earns skates,
// a keyboard/PCB earns HE switches, anything else earns the mystery gift.
export function freebieHandleForTier(tier, lines) {
        if (tier === 1) return "keycap-keychain";
        if (tier === 2) return "karambit-keychain";
        if (tier === 3) {
                const types = (lines || [])
                        .filter((l) => !isWarrantyLine(l) && !isFreebieLine(l))
                        .map((l) => String(l?.merchandise?.product?.productType || "").toLowerCase());
                if (types.some((t) => t === "mouse" || t === "mice")) return "dot-skates";
                if (types.some((t) => t.includes("keyboard") || t.includes("pcb"))) return "he-switches-4pk";
                return MYSTERY;
        }
        return null;
}

// Session suppression: a gift the shopper manually removes is declined for
// the rest of the session and never auto re-added.
const DECLINE_KEY = "loadout.freebie.declined";
function readDeclined() {
        try {
                return new Set(JSON.parse(sessionStorage.getItem(DECLINE_KEY) || "[]"));
        } catch {
                return new Set();
        }
}
export function declineFreebie(handle) {
        if (!handle) return;
        try {
                const s = readDeclined();
                s.add(handle);
                sessionStorage.setItem(DECLINE_KEY, JSON.stringify([...s]));
        } catch { /* storage unavailable — suppression simply off */ }
}
export function freebieDeclined(handle) {
        return readDeclined().has(handle);
}

// Variant resolution via the FastAPI Shopify proxy. Module-cached.
// Falls back to the mystery freebie when the preferred gift is
// unpublished / not for sale.
const _variantCache = {};
async function fetchFreebieVariantId(handle) {
        if (_variantCache[handle] !== undefined) return _variantCache[handle];
        try {
                const r = await fetch(`${API_BASE}/api/shopify/product/${handle}`, { credentials: "include" });
                if (!r.ok) {
                        _variantCache[handle] = null;
                        return null;
                }
                const p = await r.json();
                const v = p?.firstVariant?.id || p?.variants?.edges?.[0]?.node?.id || null;
                _variantCache[handle] = p?.availableForSale === false ? null : v;
        } catch {
                return null; // don't cache network errors — retry next cart change
        }
        return _variantCache[handle];
}

const _resolution = {}; // wantHandle -> { handle, variantId } | null
export async function resolveFreebie(wantHandle) {
        if (_resolution[wantHandle] !== undefined) return _resolution[wantHandle];
        let out = null;
        const v = await fetchFreebieVariantId(wantHandle);
        if (v) out = { handle: wantHandle, variantId: v };
        else if (wantHandle !== MYSTERY) {
                const m = await fetchFreebieVariantId(MYSTERY);
                if (m) out = { handle: MYSTERY, variantId: m };
        }
        _resolution[wantHandle] = out;
        return out;
}
export function resolvedHandleFor(wantHandle) {
        return _resolution[wantHandle]?.handle || wantHandle;
}
